import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useToast } from '@/components/ui/use-toast';
import { EmailAPI } from '@/services/emailAPI';
import { Eye, EyeOff, Mail, Lock, Loader2, Shield, AlertCircle } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess: () => void;
  onSwitchToSignup: () => void;
}

export function LoginForm({ onSuccess, onSwitchToSignup }: LoginFormProps) {
  const { register, handleSubmit, formState: { errors, isSubmitting }, getValues, setValue } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const onSubmit = async (data: LoginFormData) => {
    const normalizedEmail = data.email.trim().toLowerCase();

    try {
      await signInWithEmailAndPassword(auth, normalizedEmail, data.password);
      
      // Handle remember me option
      if (data.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('lastEmail', normalizedEmail);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('lastEmail');
      }
      
      toast({
        title: 'Welcome back! 👋',
        description: 'You have successfully logged in',
      });
      onSuccess();
    } catch (error: any) {
      console.error('Login error:', error);
      
      let errorMessage = 'Failed to login';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      toast({
        title: 'Login Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleForgotPassword = async () => {
    const email = getValues('email')?.trim();
    if (!email) {
      toast({
        title: 'Email Required',
        description: 'Please enter your email address first',
        variant: 'destructive',
      });
      return;
    }

    const emailValidation = z.string().email().safeParse(email);
    if (!emailValidation.success) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    setIsResettingPassword(true);
    try {
      const resetResult = await EmailAPI.requestPasswordReset(email, 'InTouch user', '');
      if (!resetResult.success) {
        throw Object.assign(new Error(resetResult.message || 'Failed to send password reset email'), {
          code: resetResult.code,
        });
      }

      toast({
        title: 'Password Reset Email Sent',
        description: 'Check your email from info@intouchsn.com for password reset instructions',
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      // Fallback to Firebase Auth default reset email when callable flow fails.
      try {
        await sendPasswordResetEmail(auth, email);
        toast({
          title: 'Password Reset Email Sent',
          description: 'Check your inbox for password reset instructions.',
        });
      } catch (fallbackError: any) {
        let errorMessage = 'Failed to send password reset email';

        if (
          fallbackError.code === 'auth/invalid-email' ||
          error.code === 'functions/invalid-argument'
        ) {
          errorMessage = 'Invalid email address';
        } else if (fallbackError.code === 'auth/user-not-found') {
          errorMessage = 'If an account exists for this email, reset instructions will be sent.';
        } else if (error.code === 'functions/failed-precondition') {
          errorMessage = 'Email service is unavailable. Using fallback also failed.';
        }

        toast({
          title: 'Reset Failed',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Pre-fill email if remembered
  React.useEffect(() => {
    const rememberMe = localStorage.getItem('rememberMe');
    const lastEmail = localStorage.getItem('lastEmail');
    if (rememberMe === 'true' && lastEmail) {
      setValue('email', lastEmail, { shouldValidate: true });
    }
  }, [setValue]);

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full mb-3">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold">Welcome Back</h3>
        <p className="text-gray-600">Sign in to your account to continue</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email address"
            {...register('email')}
            error={errors.email?.message}
            className="h-11"
            autoComplete="email"
          />
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              {...register('password')}
              error={errors.password?.message}
              className="h-11 pr-10"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              {...register('rememberMe')}
            />
            <Label htmlFor="rememberMe" className="text-sm text-gray-600">
              Remember me
            </Label>
          </div>
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={isResettingPassword}
            className="text-sm text-blue-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResettingPassword ? (
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Sending...
              </span>
            ) : (
              'Forgot password?'
            )}
          </button>
        </div>

        <Button 
          type="submit" 
          className="w-full h-11 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-medium rounded-lg shadow-lg transition-all duration-200 transform hover:scale-[1.02]" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Signing you in...
            </>
          ) : (
            'Sign In'
          )}
        </Button>
      </form>

      <div className="text-center text-sm text-gray-500">
        Don't have an account?{' '}
        <button 
          type="button"
          onClick={onSwitchToSignup}
          className="text-blue-600 hover:underline font-medium"
        >
          Create one now
        </button>
      </div>

      {/* Security Notice */}
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-2">
          <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700 dark:text-blue-300">
            Your account is protected with enterprise-grade security. We'll never share your information.
          </p>
        </div>
      </div>
    </div>
  );
}